// The /apply Meta-binding/session bookkeeping micro-library.
//
// Pure, React-free functions that exist solely to keep the Meta CAPI Lead
// dedup honest across tab reloads: minting/reading the draft id, reading and
// writing the sessionStorage lead-binding values, and the history-state "tab
// claim" that proves a reload (not a fresh tab) owns the stored lead
// identity. See application-flow.tsx for how ApplicationFlow composes these.

export const DRAFT_STORAGE_KEY = "ca_apply_draft_v1"
export const DRAFT_META_ID_KEY = "__ca_apply_draft_id"
// The step he was on when the last server save went out, kept beside his
// answers so a return visit lands where the saved row says he stopped instead
// of back on step one. The id is read first (it survives a reordered list),
// the index second.
export const DRAFT_STEP_ID_KEY = "__ca_apply_step_id"
export const DRAFT_STEP_INDEX_KEY = "__ca_apply_step_index"
export const LEAD_ID_SESSION_KEY = "ca_apply_lead_id_v1"
export const LEAD_META_EVENT_SESSION_KEY = "ca_apply_meta_lead_event_v1"
export const META_BINDING_SESSION_KEY = "ca_apply_meta_binding_v1"
const META_TAB_CLAIM_HISTORY_KEY = "__ca_apply_meta_tab_claim"

const META_EVENT_ID_RE = /^[a-f0-9]{64}$/
const LEAD_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const META_BINDING_TOKEN_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/

export function eligibleMetaEventId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const eventId = value.trim()
  return META_EVENT_ID_RE.test(eventId) ? eventId : undefined
}

export function eligibleLeadId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const leadId = value.trim()
  return LEAD_ID_RE.test(leadId) ? leadId.toLowerCase() : undefined
}

export function eligibleDraftId(value: unknown): string | undefined {
  return eligibleLeadId(value)
}

export function eligibleMetaBindingToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const token = value.trim()
  return token.length <= 512 && META_BINDING_TOKEN_RE.test(token) ? token : undefined
}

export function mintDraftId(): string | null {
  if (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function") return null
  return crypto.randomUUID().toLowerCase()
}

export function readStoredDraftId(): string | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return eligibleDraftId(parsed?.[DRAFT_META_ID_KEY]) ?? null
  } catch {
    return null
  }
}

export function isReloadNavigation(): boolean {
  if (typeof performance === "undefined") return false
  const navigation = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined
  return navigation?.type === "reload"
}

export function historyMetaTabClaim(): string | null {
  if (typeof window === "undefined") return null
  const state = window.history.state
  if (!state || typeof state !== "object") return null
  return eligibleDraftId((state as Record<string, unknown>)[META_TAB_CLAIM_HISTORY_KEY]) ?? null
}

export function writeHistoryMetaTabClaim(draftId: string): void {
  if (typeof window === "undefined") return
  try {
    const state =
      window.history.state && typeof window.history.state === "object"
        ? { ...(window.history.state as Record<string, unknown>) }
        : {}
    window.history.replaceState(
      { ...state, [META_TAB_CLAIM_HISTORY_KEY]: draftId },
      "",
    )
  } catch {
    // The signed server token remains usable in-memory for this page instance.
  }
}

export function clearHistoryMetaTabClaim(): void {
  if (typeof window === "undefined") return
  try {
    const current = window.history.state
    if (!current || typeof current !== "object") return
    const state = { ...(current as Record<string, unknown>) }
    delete state[META_TAB_CLAIM_HISTORY_KEY]
    window.history.replaceState(state, "")
  } catch {
    // Best-effort cleanup only; the session capability is cleared separately.
  }
}

export function readSessionValue(key: string): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return sessionStorage.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

export function writeSessionValue(key: string, value: string): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Storage is best-effort. The next server response can restore the binding.
  }
}

export function clearMetaLeadSession(): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.removeItem(LEAD_ID_SESSION_KEY)
    sessionStorage.removeItem(LEAD_META_EVENT_SESSION_KEY)
    sessionStorage.removeItem(META_BINDING_SESSION_KEY)
    clearHistoryMetaTabClaim()
  } catch {
    // Storage is best-effort; in-memory refs are cleared separately.
  }
}
